import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Customer } from '@/lib/types';
import { inputStyle } from '@/components/ui';

/**
 * 顧客搜尋選擇器：輸入姓名 / 電話即時比對（伺服端搜尋，支援數萬筆）。
 * 取代會把全部顧客塞進 <select> 的下拉。
 */
export function CustomerPicker({
  value,
  onChange,
  exclude,
  placeholder = '輸入姓名 / 電話搜尋…',
  initialLabel,
}: {
  value: string;
  onChange: (id: string) => void;
  exclude?: string;
  placeholder?: string;
  // 外部已預選顧客時的顯示文字(自其他頁帶入)
  initialLabel?: string;
}) {
  const [term, setTerm] = useState(value && initialLabel ? initialLabel : '');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 外部清空（例如送出後重置表單）時，連動清掉輸入框
  useEffect(() => {
    if (!value) setTerm('');
  }, [value]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const results = useQuery({
    queryKey: ['customer-search', debounced],
    enabled: open && debounced.length >= 1,
    queryFn: () => api.get<Customer[]>(`/customers?q=${encodeURIComponent(debounced)}`),
  });

  const list = (results.data ?? []).filter((c) => c.id !== exclude);

  const pick = (c: Customer) => {
    onChange(c.id);
    setTerm(`${c.name}・${c.phone}`);
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setTerm('');
    setOpen(true);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          style={inputStyle}
          value={term}
          placeholder={placeholder}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
            if (value) onChange('');
          }}
          onFocus={() => setOpen(true)}
        />
        {value && (
          <button
            type="button"
            onClick={clear}
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--ink-4)',
              fontSize: 14,
            }}
            aria-label="清除"
          >
            ×
          </button>
        )}
      </div>

      {open && debounced.length >= 1 && !value && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            maxHeight: 220,
            overflow: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 60,
          }}
        >
          {results.isLoading && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--ink-4)' }}>搜尋中…</div>
          )}
          {!results.isLoading && list.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--ink-4)' }}>查無相符顧客</div>
          )}
          {list.map((c) => (
            <button
              key={c.id}
              type="button"
              role="option"
              onClick={() => pick(c)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                border: 0,
                borderBottom: '1px solid var(--line-soft)',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 12.5,
              }}
            >
              <span style={{ fontWeight: 600 }}>{c.name}</span>
              <span className="num" style={{ color: 'var(--ink-3)', marginLeft: 8 }}>{c.phone}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
